import { Dispatch, SetStateAction, useCallback, useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";

import { SearchButton } from "./customer-story-search-button";
import { SearchCloseButton } from "./customer-story-search-close-button";

interface CustomerStorySearchBarProps {
    searchStringValue: string;
    searchInputFieldFocused: boolean;
    setSearchString: (searchString: string) => void;
    setSearchInputFieldFocused: Dispatch<SetStateAction<boolean>>;
    clearSearchField: () => void;
    runSearch: () => void;
}

export const CustomerStorySearchBar = ({
    searchStringValue,
    searchInputFieldFocused,
    setSearchString,
    setSearchInputFieldFocused,
    clearSearchField,
    runSearch
}: CustomerStorySearchBarProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const searchRef = useRef<HTMLInputElement>(null);

    const onSearchInputChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSearchString(event.target.value);
    };

    const handleSearchBarFocus = () => {
        setSearchInputFieldFocused(true);
        createAnalyticsEvent({
            origin: "web",
            platform: "web",
            product: "marketing",
            source: "none",
            userIdType: "atlassianAccount",
            eventComponent: "searchInput",
            event: "clicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "component",
            attributes: {
                url: location.href,
                page_path: location.pathname,
                eventComponent: "customerStorysearchInput"
            }
        }).fire();
    };

    const handleOnBlur = () => {
        if (searchStringValue === "") {
            setSearchInputFieldFocused(false);
        } else {
            setSearchInputFieldFocused(true);
        }
    };

    const handleSearchButtonClick = useCallback(() => {
        if (searchRef.current) {
            searchRef.current.focus();
        }

        runSearch();
    }, [runSearch]);

    const handleClearSearchButtonClick = () => {
        clearSearchField();
        if (searchRef.current) {
            searchRef.current.focus();
        }
    };

    const searchClassName = searchInputFieldFocused ? "search-active" : "";

    return (
        <form
            css={searchBarContainerStyle}
            onSubmit={(evt) => {
                evt.preventDefault();
                handleSearchButtonClick();
            }}
        >
            <input
                className={searchClassName}
                type="text"
                placeholder={intl.formatMessage({
                    id: "CustomersLandingPage.searchPlaceholder",
                    defaultMessage: "Search customer stories"
                })}
                onFocus={handleSearchBarFocus}
                onBlur={handleOnBlur}
                onChange={onSearchInputChange}
                value={searchStringValue}
                ref={searchRef}
            />
            <div className={`search-buttons-container ${searchClassName}`}>
                {searchInputFieldFocused && (
                    <>
                        <SearchCloseButton
                            onClick={handleClearSearchButtonClick}
                        />
                        <div className="search-buttons-divider" />
                    </>
                )}
                <SearchButton />
            </div>
        </form>
    );
};

const searchBarContainerStyle = css({
    "height": "42px",
    "width": "100%",
    "background": "#F0F1F2",
    "borderRadius": "40px",
    "display": "flex",
    "flexWrap": "nowrap",
    "justifyContent": "space-between",
    "padding": "0 16px",
    // Reason:  will be handled in https://hello.jira.atlassian.cloud/browse/GWP-2389
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "input": {
        "border": "none",
        "background": "transparent",
        "outline": "none",
        "width": "100%",
        "flexBasis": "90%",
        "fontSize": "14px",
        "color": "#101214",
        "fontWeight": 700,
        "::placeholder": {
            fontWeight: 400
        }
    },

    // Reason:  will be handled in https://hello.jira.atlassian.cloud/browse/GWP-2389
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "input.search-active": {
        flexBasis: "80%"
    },

    [media.below.sm]: {
        // Reason:  will be handled in https://hello.jira.atlassian.cloud/browse/GWP-2389
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "input.search-active::placeholder": {
            color: "transparent"
        }
    },

    // Reason:  will be handled in https://hello.jira.atlassian.cloud/browse/GWP-2389
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".search-buttons-container": {
        "flexBasis": "10%",
        "display": "flex",
        "justifyContent": "flex-end",
        "flexWrap": "nowrap",
        // Reason:  will be handled in https://hello.jira.atlassian.cloud/browse/GWP-2389
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".search-buttons-divider": {
            margin: "0 8px",
            alignSelf: "center",
            flexBasis: "1%",
            height: "28px",
            // eslint-disable-next-line local/ensure-wac-token-usage
            borderInlineStart: "1px solid #101214"
        }
    },

    // Reason:  will be handled in https://hello.jira.atlassian.cloud/browse/GWP-2389
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".search-buttons-container.search-active": {
        flexBasis: "20%"
    }
});
